/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaCheck, FaExclamationTriangle } from 'react-icons/fa';

import StatusBadge from '../StatusBadge';
import {
   AsideActions,
   AsideCard,
   AsideCheckItem,
   AsideChecklist,
   AsideRow,
} from '../../styles/ui/Composer';
import { GhostButton, PrimaryButton } from '../../styles/ui/Button';

// ===========================================================================
// Cartão lateral "Resumo" das telas de escrita de movimento (2026-09-12,
// redesenho — anatomia do FC7 cap.2 §H.3, mesma do composer de
// Transferências): resumo do header + checklist de pendências + ações.
//
// O checklist é INFORMATIVO e espelha o que desabilita o salvar — quem bloqueia
// é o botão, não o item da lista (§H.3.1). Pendência de saldo negativo NÃO
// entra aqui: no módulo, saldo negativo é aviso não-bloqueante (0093 §5a), e
// tratá-lo como pendência sugeriria uma trava que não existe.
// ===========================================================================

const Head = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   margin-bottom: 12px;

   h2 {
      margin: 0;
      font-size: 15px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.textPrimary};
   }
`;

const Caption = styled.p`
   margin: 14px 0 0;
   padding-top: 12px;
   border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
   font-size: 11px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.06em;
   color: ${({ theme }) => theme.colors.textMuted};
`;

const Note = styled.p`
   margin: 10px 0 0;
   font-size: 11px;
   line-height: 1.5;
   color: ${({ theme }) => theme.colors.textMuted};
`;

export default function MovementSummaryAside({
   title = 'Resumo',
   typeLabel,
   typeVariant = 'neutral',
   rows,
   checklist,
   primaryLabel,
   onPrimary,
   primaryDisabled = false,
   primaryTitle,
   secondaryLabel,
   onSecondary,
   secondaryDisabled = false,
   note,
   children,
}) {
   const pending = checklist.filter((entry) => !entry.ok).length;

   return (
      <AsideCard>
         <Head>
            <h2>{title}</h2>
            {typeLabel ? (
               <StatusBadge variant={typeVariant}>{typeLabel}</StatusBadge>
            ) : null}
         </Head>

         {rows.map((row) => (
            <AsideRow key={row.label}>
               <span className="lbl">{row.label}</span>
               <span className="val">{row.value || '—'}</span>
            </AsideRow>
         ))}

         <Caption>Pendências</Caption>
         <AsideChecklist>
            {checklist.map((entry) => (
               <AsideCheckItem key={entry.label} $ok={entry.ok}>
                  <span className="ic" aria-hidden="true">
                     {entry.ok ? (
                        <FaCheck size={11} />
                     ) : (
                        <FaExclamationTriangle size={11} />
                     )}
                  </span>
                  {entry.label}
               </AsideCheckItem>
            ))}
         </AsideChecklist>

         {children}

         <AsideActions>
            <PrimaryButton
               type="button"
               onClick={onPrimary}
               disabled={primaryDisabled}
               title={
                  primaryTitle ??
                  (pending
                     ? 'Resolva as pendências acima para salvar'
                     : undefined)
               }
            >
               {primaryLabel}
            </PrimaryButton>

            {secondaryLabel ? (
               <GhostButton
                  type="button"
                  onClick={onSecondary}
                  disabled={secondaryDisabled}
               >
                  {secondaryLabel}
               </GhostButton>
            ) : null}
         </AsideActions>

         {note ? <Note>{note}</Note> : null}
      </AsideCard>
   );
}

MovementSummaryAside.propTypes = {
   title: PropTypes.string,
   typeLabel: PropTypes.string,
   typeVariant: PropTypes.oneOf(['neutral', 'blue', 'green', 'amber', 'red']),
   rows: PropTypes.arrayOf(
      PropTypes.shape({
         label: PropTypes.string.isRequired,
         value: PropTypes.node,
      })
   ).isRequired,
   checklist: PropTypes.arrayOf(
      PropTypes.shape({
         label: PropTypes.string.isRequired,
         ok: PropTypes.bool.isRequired,
      })
   ).isRequired,
   primaryLabel: PropTypes.string.isRequired,
   onPrimary: PropTypes.func.isRequired,
   primaryDisabled: PropTypes.bool,
   primaryTitle: PropTypes.string,
   secondaryLabel: PropTypes.string,
   onSecondary: PropTypes.func,
   secondaryDisabled: PropTypes.bool,
   note: PropTypes.node,
   // Slot entre o checklist e as ações — os hints dos warn guards vivem aqui.
   children: PropTypes.node,
};
